import React, { PureComponent, Fragment } from 'react';
import { Grid, Icon, Button, Popup } from 'semantic-ui-react';
import classNames from 'classnames';
import { Droppable } from 'react-beautiful-dnd';
import { get } from 'lodash';
import { Draggable } from 'react-beautiful-dnd';

import {
  EDIT_MENU_TYPES,
  EDIT_MENU_QUERYSTRINGS,
} from 'Pages/managemenu/helpers/Constants';
import 'Styles/pages/managemenu/dailyView.less';

import { ViewPropTypes } from './ViewPropTypes';
import { DailyContributions } from './DailyContributions';
import {
  groupItemsByCategory,
  getNumberOfWeeks,
  shouldAllowCombine,
  calculateServingsPercentage,
  formatDayText,
  canEditAppliedMenu,
  getDaysToShow,
} from '../utilities';
import { VIEW_BY } from '../constants';
import { DailyViewCategoryItem } from './DailyViewCategoryItem';

class DailyView extends PureComponent {
  static propTypes = ViewPropTypes;

  static defaultProps = {
    onDeleteClick: () => {},
    onBulkDeleteClick: () => {},
    onHideClick: () => {},
    onBulkHideClick: () => {},
    disableHover: true,
    onProjectedServingsChange: (draggableId) => () => {},
    onProjectedServingsBlur: (draggableId) => () => {},
    onQtyOfPortionChange: (draggableId) => () => {},
    onQtyOfPortionBlur: (draggableId) => () => {},
    onProjectedServingsPercentageChange: () => {},
    showDaysOfTheWeek: false,
    showMealContributionReq: false,
    openRecipe: () => {},
    currentDay: 1,
  };

  onWeekButtonClick = (dayNumber) => () => {
    const { onDayViewDayChange } = this.props;
    onDayViewDayChange(dayNumber);
  };

  isItemSelected = (id) => !!this.props.selectedRecipesById[id];
  isItemInactive = (inactive) => inactive === 'Y';
  isItemCopied = (id) => !!this.props.copiedRecipesById[id];

  renderWeekHeaderButtons() {
    const {
      weekNumber,
      analysisDays,
      numberOfDays,
      showDaysOfTheWeek,
      currentDay,
      menuType,
      queryParams,
      editableAppliedDays,
      showMealContributionReq,
      schoolCalendar,
    } = this.props;
    const buttons = [];

    const daysToShow = getDaysToShow(
      numberOfDays,
      queryParams[EDIT_MENU_QUERYSTRINGS.START_DATE],
      analysisDays,
      schoolCalendar
    );

    const beginIndex = (weekNumber - 1) * analysisDays;
    const endingDay = weekNumber * analysisDays;
    const endIndex =
      endingDay > daysToShow.length ? daysToShow.length : endingDay;

    const daysForGivenWeek = daysToShow.slice(beginIndex, endIndex);

    daysForGivenWeek.forEach((dayNumber) => {
      const isSelected = dayNumber === currentDay;
      const requirements = this.getDayRequirements(dayNumber);
      const dayRequirementsMet = this.checkIfDailyRequirementsMet(requirements);

      const formatArgs = {
        weekNumber,
        showDaysOfTheWeek,
        analysisDays,
        dayNumber,
      };

      if (menuType === EDIT_MENU_TYPES.APPLIED_MENU) {
        formatArgs.startDate = queryParams[EDIT_MENU_QUERYSTRINGS.START_DATE];
      }
      const canEdit = canEditAppliedMenu({
        menuType,
        editableAppliedDays,
        dayNumber,
      });

      buttons.push(
        <div
          key={dayNumber - 1}
          className={classNames('week-button cursorPointer', {
            active: isSelected,
            'requirements-met':
              canEdit && showMealContributionReq && dayRequirementsMet,
            'requirements-not-met':
              canEdit && showMealContributionReq && !dayRequirementsMet,
          })}
          onClick={this.onWeekButtonClick(dayNumber)}
        >
          <div>{formatDayText(formatArgs)}</div>
          <Popup
            content="Reimbursable Meals"
            trigger={
              <Button
                className="create-reimb-meal-btn"
                onClick={this.onCreateReimbMealsClick(dayNumber)}
              >
                <Icon className="food" />
              </Button>
            }
          />
        </div>
      );
    });

    return buttons;
  }

  onCreateReimbMealsClick = (dayNumber) => (e) => {
    const { showReimbMealsModal } = this.props;

    e.stopPropagation();
    showReimbMealsModal(dayNumber);
  };

  renderSideWeekButtons() {
    const {
      numberOfDays,
      analysisDays,
      currentWeek,
      onWeekChange,
      weeklyRequirements,
      onDayViewDayChange,
      queryParams,
      schoolCalendar,
    } = this.props;

    const daysToShow = getDaysToShow(
      numberOfDays,
      queryParams[EDIT_MENU_QUERYSTRINGS.START_DATE],
      analysisDays,
      schoolCalendar
    );
    const numberOfWeeks = getNumberOfWeeks(daysToShow.length, analysisDays);

    const buttons = [];

    for (let i = 0; i < numberOfWeeks; i++) {
      let className = classNames('side-button', {
        active: currentWeek - 1 === i,
      });
      const weekNumber = i + 1;

      if (Array.isArray(weeklyRequirements) && weeklyRequirements.length > 0) {
        const requirements = get(weeklyRequirements[i], 'List', []);
        const weeklyRequirementsMet = requirements.every(
          (req) => req.WeeklyMet
        );
        className = classNames(className, {
          'requirements-met': weeklyRequirementsMet,
          'requirements-not-met': !weeklyRequirementsMet,
        });
      } else {
        className = classNames(className, 'requirements-met');
      }

      buttons.push(
        <div
          className={className}
          key={i}
          onClick={() => {
            onDayViewDayChange(1 + i * analysisDays);
            onWeekChange(weekNumber);
          }}
        >
          {weekNumber}
        </div>
      );
    }

    return buttons;
  }

  getCategoryTotalServings = (categoryItems) => {
    let total = 0;

    categoryItems.forEach((item) => {
      total += item.ProjectedServings || 0;
    });

    return total;
  };

  onItemPercentageChange = (item) => (percentage) => {
    const { plannedServings, onProjectedServingsPercentageChange } = this.props;
    const { draggableId } = item;

    onProjectedServingsPercentageChange(
      draggableId,
      Math.round((plannedServings * percentage) / 100)
    );
  };

  computeItemPercentage = (item) => {
    const { plannedServings } = this.props;

    return calculateServingsPercentage({
      projectedServings: item.ProjectedServings,
      servings: plannedServings,
    });
  };

  sumCategoryItemPercentage(items) {
    return Number(
      items
        .reduce((accumulator, currentItem) => {
          return this.computeItemPercentage(currentItem) + accumulator;
        }, 0)
        .toFixed(3)
    );
  }

  onDeleteClick = (recipe) => (e) => {
    const { onDeleteClick } = this.props;
    e.stopPropagation();

    onDeleteClick(recipe);
  };

  onBulkDeleteRecipesClick = (recipes) => (e) => {
    const { onBulkDeleteClick } = this.props;

    onBulkDeleteClick(recipes);
  };

  onBulkHideRecipesClick = (recipes) => (e) => {
    const { onBulkHideClick } = this.props;
    const allVisible = this.checkIfAllRecipesVisible(recipes);

    const items = recipes.map((recipe) => ({
      recipeDraggableId: recipe.draggableId,
      displayOnCalendar: !allVisible,
    }));

    onBulkHideClick(items);
  };

  onHideClick = (recipeDraggableId, displayOnCalendar) => (e) => {
    const { onHideClick } = this.props;
    e.stopPropagation();
    onHideClick(recipeDraggableId, !displayOnCalendar);
  };

  onItemClick = (id) => (e) => {
    const {
      selectedRecipesById,
      selectMenuItem,
      clearSelection,
      isReadOnly,
    } = this.props;

    if (isReadOnly) {
      return;
    }

    if (!Object.keys(selectedRecipesById).length) {
      selectMenuItem(id);
    }

    if (Object.keys(selectedRecipesById).length) {
      if (!e.metaKey && !e.ctrlKey) {
        clearSelection();
      }
      selectMenuItem(id);
    }

    e.stopPropagation();
  };

  onRemoveChild = (childId, parentId) => (e) => {
    const { removeChild } = this.props;
    removeChild(childId, parentId);
    e.stopPropagation();
  };

  renderItem = (item, index) => {
    const {
      isReadOnly,
      disableHover,
      onProjectedServingsChange,
      onProjectedServingsBlur,
      onQtyOfPortionChange,
      onQtyOfPortionBlur,
      recipesById,
      isDragging,
      draggingMeta,
      onProjectedServingsPercentageChange,
      onProjectedServingsPercentageBlur,
      onToggleRecipeAlacarte,
      recipeCountByDayMap,
      openRecipe,
      lassoMode,
    } = this.props;

    return (
      <DailyViewCategoryItem
        key={index}
        item={item}
        isItemSelected={this.isItemSelected}
        isItemCopied={this.isItemCopied}
        onQtyOfPortionChange={onQtyOfPortionChange}
        onQtyOfPortionBlur={onQtyOfPortionBlur}
        onProjectedServingsChange={onProjectedServingsChange}
        onProjectedServingsBlur={onProjectedServingsBlur}
        onHideClick={this.onHideClick}
        onItemPercentageChange={this.onItemPercentageChange}
        onItemClick={this.onItemClick}
        onDeleteClick={this.onDeleteClick}
        onProjectedServingsPercentageChange={
          onProjectedServingsPercentageChange
        }
        onProjectedServingsPercentageBlur={onProjectedServingsPercentageBlur}
        isReadOnly={isReadOnly}
        disableHover={disableHover}
        onToggleAlacarte={onToggleRecipeAlacarte}
        computeItemPercentage={this.computeItemPercentage}
        onDoubleClick={(item) => openRecipe(item.RecipeId)}
        recipesById={recipesById}
        isDragging={isDragging}
        draggingMeta={draggingMeta}
        onRemoveChild={this.onRemoveChild}
        recipeCountByDayMap={recipeCountByDayMap}
        isItemInactive={this.isItemInactive}
        openRecipe={openRecipe}
        lassoMode={lassoMode}
      />
    );
  };

  renderCategoryItems(items) {
    return items.map(this.renderItem);
  }

  checkIfAllRecipesVisible(recipes) {
    return recipes.every((recipe) => recipe.DisplayOnCalendar);
  }

  checkIfDailyRequirementsMet(requirements) {
    return requirements.length === 0;
  }

  getDayRequirements(day) {
    const { unmetDailyRequirements } = this.props;

    return get(unmetDailyRequirements, `[${day - 1}].List`, []);
  }

  render() {
    const {
      menuDaysMap,
      recipesById,
      recipeCategories,
      plannedServings,
      showMealContributionReq,
      dailyContributions,
      currentDay,
      draggingMeta,
      searchResults,
      searchDroppableId,
      editableAppliedDays,
      menuType,
      isReadOnly,
      copiedDays,
    } = this.props;

    const itemsByCategory = groupItemsByCategory(
      menuDaysMap[currentDay],
      recipesById
    );

    const requirements = this.getDayRequirements(currentDay);

    let isCombineEnabled = false;
    const draggingId = get(draggingMeta, 'draggableId');

    if (draggingId) {
      let recipe = null;
      if (get(draggingMeta, 'source.droppableId') === searchDroppableId) {
        const index = get(draggingMeta, 'source.index');
        recipe = get(searchResults, index);
      } else {
        recipe = recipesById[draggingId];
      }
      isCombineEnabled = shouldAllowCombine(recipe);
    }

    const canEdit = canEditAppliedMenu({
      menuType,
      editableAppliedDays,
      dayNumber: currentDay,
    });

    const copied = copiedDays[currentDay];

    return (
      <Grid padded className="daily-menu-view">
        <Grid.Row>
          <Grid.Column width={16} className="daily-menu-section">
            <div className="daily-week-headers">
              {this.renderWeekHeaderButtons()}
            </div>
            <div className={classNames('daily-menu-container', { copied })}>
              <div className="side-button-container">
                {this.renderSideWeekButtons()}
              </div>
              <Droppable
                droppableId={`${currentDay}`}
                isCombineEnabled={isCombineEnabled}
              >
                {(provided, snapshot) => (
                  <Fragment>
                    <div className="daily-menu" ref={provided.innerRef}>
                      <div className="daily-menu-row daily-menu-header">
                        <div className="daily-category-column">Category</div>
                        <div className="daily-menu-column meal-contribution ">
                          Meal Contributions
                        </div>
                        <div className="daily-menu-column serving-size">
                          Serving Size
                        </div>
                        <div className="daily-menu-column planned-servings">
                          Planned
                        </div>
                        <div className="daily-menu-column servings-percentage">
                          %
                        </div>
                        <div className="daily-menu-column alacarte">
                          A la carte
                        </div>
                        {!isReadOnly && (
                          <div className="daily-menu-right-icons" />
                        )}
                      </div>
                      <div className="daily-menu-item-list">
                        {recipeCategories.map((category, i) => {
                          const { Id, Description } = category;
                          let categoryItems = itemsByCategory[Id] || [];

                          const parentItems = categoryItems.filter(
                            (item) => item.level === 0
                          );

                          if (parentItems.length === 0) {
                            return null;
                          }

                          const firstItemInCategory = parentItems[0];

                          const totalServings = this.getCategoryTotalServings(
                            categoryItems
                          );
                          const allRecipesHidden = categoryItems.every(
                            (item) => !item.DisplayOnCalendar
                          );
                          const totalPercentage = this.sumCategoryItemPercentage(
                            categoryItems
                          );
                          const percentageHeaderClass = classNames(
                            'daily-menu-column servings-percentage',
                            {
                              'percentage-met': totalPercentage === 100,
                              'percentage-not-met': totalPercentage !== 100,
                            }
                          );

                          return (
                            <React.Fragment
                              key={`${currentDay}-${category.Id}`}
                            >
                              <Draggable
                                index={firstItemInCategory.OrderNum - 1}
                                draggableId={`${currentDay}-${category.Id}`}
                                isDragDisabled={true}
                                disableInteractiveElementBlocking={true}
                              >
                                {(provided, snapshot) => {
                                  return (
                                    <div
                                      className="daily-menu-row daily-menu-category"
                                      ref={provided.innerRef}
                                      {...provided.draggableProps}
                                      {...provided.dragHandleProps}
                                    >
                                      <div className="daily-category-column">
                                        {Description}
                                      </div>
                                      <div className="daily-menu-column meal-contribution " />
                                      <div className="daily-menu-column serving-size" />
                                      <div className="daily-menu-column planned-servings-header">
                                        {plannedServings}
                                      </div>
                                      <div className={percentageHeaderClass}>
                                        {this.sumCategoryItemPercentage(
                                          categoryItems
                                        )}
                                        % ({totalServings})
                                      </div>
                                      <div className="daily-menu-column alacarte" />
                                      {!isReadOnly && (
                                        <div className="daily-menu-right-icons">
                                          <Icon
                                            className={classNames({
                                              eye: !allRecipesHidden,
                                              'fa-eye-slash': allRecipesHidden,
                                            })}
                                            onClick={this.onBulkHideRecipesClick(
                                              parentItems
                                            )}
                                          />
                                          <Icon
                                            className="trash"
                                            onClick={this.onBulkDeleteRecipesClick(
                                              parentItems
                                            )}
                                          />
                                        </div>
                                      )}
                                    </div>
                                  );
                                }}
                              </Draggable>

                              {this.renderCategoryItems(parentItems)}
                            </React.Fragment>
                          );
                        })}
                      </div>
                      {provided.placeholder}
                      {canEdit &&
                        showMealContributionReq &&
                        requirements.map((item, i) => (
                          <div key={i} className="daily-menu-requirement">
                            {item}
                          </div>
                        ))}
                    </div>
                  </Fragment>
                )}
              </Droppable>
            </div>
            <div className="daily-contribtion-container">
              <DailyContributions
                contributions={dailyContributions[currentDay - 1]}
                viewType={VIEW_BY.DAY}
              />
            </div>
          </Grid.Column>
        </Grid.Row>
      </Grid>
    );
  }
}

export { DailyView };



// WEBPACK FOOTER //
// ./src/pages/managemenu/subpages/editmenu/components/DailyView.js